import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import styled from 'styled-components';
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from './routes';
import HomePage from './pages/HomePage/HomePage';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import { Pane } from "tweakpane";
import { AnimatePresence } from 'framer-motion';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import useGlobalStore from './utils/store';
import { ScenesManager } from './webgl/scenes/ScenesManager';
import { SoundController } from 'webgl/controllers/SoundController';
import { delay } from 'utils/time';
import usePageVisibility from 'utils/hooks/usePageVisibility';
import Header from 'components/Header/Header';
import LandingScreen from 'components/LandingScreen/LandingScreen';
import { APP_EVENTS, EventsManager } from 'webgl/events/EventsManager';
import { Event } from "utils/services/eventManager.service";
import { CameraManager } from 'webgl/scenes/CameraManager';
import { COPY, eventDates } from 'data/content';
import { parseUrlQuery } from 'utils/url';
import axios from "axios";
import TitleComponent from 'components/TitleComponent/TitleComponent';
import { getFeaturesData, setFeaturesData } from 'utils/localStorage';
import { SpriteManager } from 'webgl/scenes/objects/SpriteManager';
import { Vector3 } from 'three';
import { UIManager } from 'webgl/scenes/UIManager';
import { supportedLanguages } from 'data/content.types';
import ButtonSound from 'components/ButtonSound/ButtonSound';
import { SOUND_LABELS } from 'data/sound';
import PreSaleOverlay from 'components/PreSaleOverlay/PreSaleOverlay';

// Define estilos directamente aquí
const Styled = {
  Wrapper: styled.div`
    width: 100vw;
    height: 100vh;
  `,
  Canvas: styled.canvas`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    transition: opacity 0.6s;
  `,
  Video: styled.video`
    position: absolute;
    width: 0;
    height: 0;
    top: -1px;
    left: -1px;
  `
};

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

declare global {
  interface Window {
    panel: any;
    getCameraCoordinates: () => void;
  }
}

function App() {
  const location = useLocation();

  const { 
    isLoading, 
    setIsLoading, 
    setShowUI, 
    showUI,
    setLoadedPercentage, 
    setCopy,
    copy, 
    setIsPreEvent, 
    setHasAllFeatures, 
    setIsEventLive,
    setIsContentLoaded,
    isContentLoaded,
    setSceneMounted,
    sceneMounted,
    setIsPreVenda, 
    isPreVenda,
    setIsCTA, 
    isCTA,
    setShowPreSaleOverlay, 
    showPreSaleOverlay, 
    setIsCountdown
  } = useGlobalStore();
  const navigate = useNavigate();
  const isVisible = usePageVisibility();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [inScene, setInScene] = useState<boolean>(false);
  const isAppMountedRef = useRef<boolean>(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);  
  const eventRef = useRef<Date>(new Date(copy.event.eventDay));
  const editFeaturesPaneRef = useRef<any>();
  // const todayRef = useRef<Date>(new Date());

  const backToInitial = () => {
    if (!CameraManager.enabled()) {
      CameraManager.goFromFeature();
    }
  }

  const contentParse = (data: Array<{
    id: string;
    enabled: boolean;
    date?: string | null;
    label?: string | null;
    label2?: string | null;
    url?: string | null;
  }>) => {
    return new Promise(resolve => {
      data.forEach((content) => {
        if (content.id === 'carro_revelado') {
          ScenesManager.EXTERNAL_REVEALEAD = content.enabled;
        }
        else if (content.id === 'interno_revelado') {
          ScenesManager.INTERNAL_REVEALEAD = content.enabled;
        }
        else if (content.id === 'pre_venda' && content.enabled) {
          if (content.label && content.url) {
            COPY[supportedLanguages.es].homeScreen.eventCtaPre = content.label;
            COPY[supportedLanguages.es].homeScreen.eventCtaPreURL = content.url;
            setIsPreVenda(true);
          }
        }
        else if (content.id === 'cta' && content.enabled) {
          if (content.label && content.url) {
            COPY[supportedLanguages.es].homeScreen.learnMoreCta = content.label;
            COPY[supportedLanguages.es].homeScreen.learnMoreCtaURL = content.url;
            setIsCTA(true);
          }
        }
        else if (content.id === 'countdown' && content.enabled) {
          if (content.label && content.label2 && content.date) {
            COPY[supportedLanguages.es].preSale.date = content.date.replace('0-03:00', '');
            COPY[supportedLanguages.es].preSale.title = `<span>${content.label}</span><br>${content.label2}`;
            setShowPreSaleOverlay(true);
            setIsCountdown(true);
          }
        }
      });
      resolve(true);
    });
  }
  useEffect(() => {     
    if (!isAppMountedRef.current) { 
    
    isAppMountedRef.current = true;
    
    const lsData = getFeaturesData();
   

    const apiUrl = "/data/production_features.json";
            
    const featuresPromise = axios.get(apiUrl)
    .then(async (res) => {
      if (lsData && parseUrlQuery().editMode !== undefined) {
        const parse = JSON.parse(JSON.parse(lsData));
        await ScenesManager.featuresParse([parse]);    
      } else {
        await ScenesManager.featuresParse(res.data);    
      }
    }).catch((e) => console.log(e));

    const apiContentUrl = "/data/production_content.json";
            
    const contentApiPromise = axios.get(apiContentUrl)
    .then(async (res) => {
      console.log(res.data);
      await contentParse(res.data);
    }).catch((e) => console.log(e));

    Promise.all([featuresPromise, contentApiPromise])
      .then(() => {
        setIsContentLoaded(true);
      })
      .catch((error) => {
        console.error("Error loading content:", error);
      });

    EventsManager.instance.on(APP_EVENTS.FEATURES_COUNT_COMPLETE, (details: Event) => {
    
      // setIsPreEvent(details.data.total < ScenesManager.TOTAL_FEATURES.external);
      // eventRef.current = details.data.total <= ScenesManager.TOTAL_FEATURES.external ? 
      //   new Date(eventDates.event.date) : 
      //   new Date(eventDates.preSale.date);
      // setIsEventLive(
      //   details.data.total >= ScenesManager.TOTAL_FEATURES.external && 
      //   details.data.total < ScenesManager.TOTAL_FEATURES.all
      // );
  
      setHasAllFeatures(details.data.total === ScenesManager.TOTAL_FEATURES.all);
  
      const isPreEvenDebug = parseUrlQuery().isPreEvent === "true";
      const isEventLiveDebug = parseUrlQuery().isEventLive === "true";
      const hasAllFeaturesDebug = parseUrlQuery().hasAllFeatures === "true";
      if (isPreEvenDebug) setIsPreEvent(true);
      if (isEventLiveDebug) {
        setIsEventLive(true);
        setIsPreEvent(false)
      }
      if (hasAllFeaturesDebug) setHasAllFeatures(true);
    });
  }
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {

    
    if (!isLoading && isContentLoaded) {
      // ScenesManager.buildCar();   
      ScenesManager.buildScene(); 

      // buildScene();

    const asyncEffect = async () => {
      await delay(0.5);
      setLoadedPercentage(0.96);
      await delay(0.5);
      setLoadedPercentage(0.98);
      await delay(1);
      setLoadedPercentage(1);
      setSceneMounted(true);
    };
    
    asyncEffect();
  }

  }, [isLoading, isContentLoaded])


  useEffect(() => {    
    if (!isMuted) SoundController.mute(!isVisible);
  }, [isVisible]);

  return (
    <Styled.Wrapper>
      <Styled.Canvas
        onClick={backToInitial}
        style={{ opacity: sceneMounted ? 1 : 0 }}
        ref={async (ref) => {
          if (canvasRef.current) return;
          canvasRef.current = ref;
          if (canvasRef.current) {
            // await delay(3)
            
            await ScenesManager.setup(canvasRef.current, (perc) => {
              setLoadedPercentage(perc);
            });
            await SoundController.setupSounds();
            setLoadedPercentage(0.9);
            EventsManager.instance.trigger(APP_EVENTS.APP_SHOW);
            setIsLoading(false);
            
          }
        }}
      />
      <Header />
      {showUI && sceneMounted && (
        <ButtonSound
          key="button_sound"
          onAction={() => {
            setIsMuted(!isMuted);
            SoundController.mute(!isMuted);
          }}
          $isPlaying={!isMuted}
          $isVisible={sceneMounted}
        />
      )}
      {/* @ts-ignore */}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route index path={ROUTES.HOME} element={<HomePage />} />
          {/* <Route path="*" element={<NotFoundPage />} /> */}
        </Routes>
      </AnimatePresence>

      {/* @ts-ignore */}
      <AnimatePresence>
        {!sceneMounted && <LoadingScreen />}
        {sceneMounted && (
          <>
            {!inScene &&
              <LandingScreen
                onCompleteLoad={() => {
                  navigate(`${location.pathname}${location.search}`, {
                    state: null,
                  });
                  setInScene(true);
                  setShowUI(true);
                }}
                />
              }
              <TitleComponent 
                onCompleteLoad={() => {
                  navigate(`${location.pathname}${location.search}`, {
                    state: null,
                  });
                  setInScene(true);
                  setShowUI(true);
                  SoundController.playAudio(SOUND_LABELS.MAIN);
                }}
              />
          </>
        )}
      </AnimatePresence>

      {(showPreSaleOverlay && !isPreVenda) && <PreSaleOverlay onClose={() => {
        setShowPreSaleOverlay(false);
        window.dataLayer?.push({
          event: 'clique_pre_venda'
        })
        EventsManager.instance.trigger(APP_EVENTS.OUTLINE_SHOW);
        navigate(`${location.pathname}${location.search}`, {
          state: null,
        });
        setInScene(true);
        setShowUI(true);
        SoundController.playAudio(SOUND_LABELS.MAIN);
        // setShowPreSaleOverlay(false);
        // ScenesManager.hasOverlay = false;
        // window.dataLayer?.push({
        //   event: 'clique_pre_venda'
        // })
      }}/>}
      
    </Styled.Wrapper>
  );
}

export default App;
